import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { useTranslation } from 'react-i18next';

import { SingleBallot } from '@asd-stan/ballot/domain/ballot.entity';
import { getBallotService } from '@asd-stan/ballot/infrastructure/getters';
import { ToasterStatus } from '@asd-stan/shell/domain/toaster.service';
import { getToasterService } from '@asd-stan/shell/infrastructure/getters';
import { ExportToasterBody } from '@asd-stan/standard/components/standard-list/standard-list.styled';
import { ReactComponent as ExportIcon } from '@asd-stan/ui-kit/assets/asd-stan-works/icons/export-icon.svg';
import { Button } from '@components/button/button';

import {
	DownloadAbortedError,
	buildResultsFileName,
	downloadBallotResults,
} from './pdf/download-ballot-results';

const bytesInMegabyte = 1024 * 1024;

const formatSize = (bytes: number) => {
	if (bytes <= 0) {
		return '';
	}
	if (bytes < bytesInMegabyte) {
		return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	}
	return `${(bytes / bytesInMegabyte).toFixed(1)} MB`;
};

const DownloadToasterBody = ({
	ballotId,
	ballot,
}: {
	ballotId: number;
	ballot: SingleBallot;
}) => {
	const { t } = useTranslation();
	const ballotService = getBallotService();
	const toasterService = getToasterService();
	const [percent, setPercent] = useState(0);
	const [totalBytes, setTotalBytes] = useState(0);
	// Whether the export ends up a zip or a lone PDF only becomes clear once the
	// votes are in, so the name starts as a placeholder and onStart corrects it.
	const [fileName, setFileName] = useState(() => buildResultsFileName(ballotId, new Date(), 'zip'));

	useEffect(() => {
		const abortController = new AbortController();
		const { signal } = abortController;

		const run = async () => {
			try {
				const data = await ballotService.getBallotResultsExport(ballotId, ballot, signal);
				if (signal.aborted) {
					return;
				}

				const { missing } = await downloadBallotResults({
					ballotId,
					data,
					signal,
					onStart: ({ fileName: nextFileName, totalBytes: nextTotal }) => {
						setFileName(nextFileName);
						setTotalBytes(nextTotal);
					},
					onProgress: ({ percent: nextPercent }) => setPercent(nextPercent),
				});

				if (signal.aborted) {
					return;
				}

				toasterService.hideToast();

				// Skipped attachments are marked inside the document, but the user is
				// told here as well so an incomplete archive is never a silent one.
				if (missing.length) {
					toasterService.showToast(
						ToasterStatus.warning,
						t('ballot.singleBallot.downloadResultsPartial', { count: missing.length })
					);
				}
			} catch (error) {
				if (signal.aborted || error instanceof DownloadAbortedError) {
					return;
				}
				console.error(error);
				toasterService.hideToast();
				toasterService.showToast(
					ToasterStatus.error,
					t('ballot.singleBallot.downloadResultsError')
				);
			}
		};

		run();

		return () => {
			abortController.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const size = formatSize(totalBytes);

	return (
		<ExportToasterBody>
			<div style={{ height: 20, width: 20 }}>
				<CircularProgressbar
					value={percent}
					maxValue={100}
					styles={buildStyles({
						pathColor: '#0B93E3',
						trailColor: '#DAE4E8',
					})}
				/>
			</div>
			<p>
				<span className="file-name">{fileName}</span>
				{size ? ` (${size})` : ''} {t('standard.standardList.isExporting')}
			</p>
			<span className="cancel" onClick={() => toasterService.hideToast()}>
				{t('standard.standardList.cancelExport')}
			</span>
		</ExportToasterBody>
	);
};

export const DownloadResultsButton = observer(
	({ ballotId, ballot }: { ballotId: number; ballot: SingleBallot }) => {
		const { t } = useTranslation();
		const toasterService = getToasterService();

		return (
			<Button
				secondary
				icon={<ExportIcon />}
				title={t('ballot.singleBallot.downloadResults')}
				disabled={toasterService.isExporting}
				onClick={() =>
					toasterService.showToast(
						ToasterStatus.export,
						<DownloadToasterBody ballotId={ballotId} ballot={ballot} />
					)
				}
			/>
		);
	}
);
